import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { SettingsLayout, UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { microsoftAuthApiRef, googleAuthApiRef, configApiRef, useApi } from '@backstage/core-plugin-api';
import { themes, UnifiedThemeProvider } from '@backstage/theme';
import { devTheme, modernTheme } from './themes';

import { MySpacePage } from '@cegid/backstage-plugin-my-space';
import { AzureMetricsPage } from '@cegid/backstage-plugin-azure-metrics';
import { AzureCostPage } from '@cegid/backstage-plugin-azure-cost';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { NotificationsPage, UserNotificationSettingsCard } from '@backstage/plugin-notifications';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { EntityValidationPage } from '@backstage-community/plugin-entity-validation';
import WbSunny from '@material-ui/icons/WbSunny';
import NightsStay from '@material-ui/icons/NightsStay';
import Code from '@material-ui/icons/Code';
import Brush from '@material-ui/icons/Brush';

import { SignalsDisplay } from '@backstage/plugin-signals';

import { explorePage } from './components/explore/ExplorePage';

const AUTH_ENVIRONMENT_LOCAL = 'local';
const AUTH_ENVIRONMENT_PREVIEW = 'preview';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },

  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);

      let authProviders;
      try {
        const authEnvironment = configApi.getString('auth.environment');

        if (authEnvironment === AUTH_ENVIRONMENT_LOCAL) {
          // Local: Google only
          authProviders = [{
            id: 'google-auth-provider',
            title: 'Google',
            message: 'Sign in using Google',
            apiRef: googleAuthApiRef
          }];
        } else if (authEnvironment === AUTH_ENVIRONMENT_PREVIEW) {
          // Preview: Both Google and Microsoft
          authProviders = [
            {
              id: 'google-auth-provider',
              title: 'Google',
              message: 'Sign in using Google',
              apiRef: googleAuthApiRef
            },
            {
              id: 'microsoft-auth-provider',
              title: 'Microsoft',
              message: 'Sign in using Microsoft',
              apiRef: microsoftAuthApiRef
            }
          ];
        } else {
          // Production: Microsoft only
          authProviders = [{
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in using Microsoft',
            apiRef: microsoftAuthApiRef
          }];
        }
      } catch (error) {
        // Failed to read auth.environment from config, defaulting to Microsoft auth
        // Log the error for debugging purposes
        // eslint-disable-next-line no-console
        console.warn('Failed to read auth.environment from config, defaulting to Microsoft auth:', error);

        // Default to Microsoft if config is missing
        authProviders = [{
          id: 'microsoft-auth-provider',
          title: 'Microsoft',
          message: 'Sign in using Microsoft',
          apiRef: microsoftAuthApiRef
        }];
      }

      return <SignInPage {...props} auto providers={authProviders} />;
    }
  },
  themes: [{
    id: 'light',
    title: 'Light',
    variant: 'light',
    icon: <WbSunny />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={themes.light}>{children}</UnifiedThemeProvider>
    ),
  },
  {
    id: 'dark',
    title: 'Dark',
    variant: 'dark',
    icon: <NightsStay />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={themes.dark}>{children}</UnifiedThemeProvider>
    ),
  },
  {
    id: 'dev',
    title: 'Dev',
    variant: 'dark',
    icon: <Code />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={devTheme}>{children}</UnifiedThemeProvider>
    ),
  },
  {
    id: 'modern',
    title: 'Modern',
    variant: 'dark',
    icon: <Brush />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={modernTheme}>{children}</UnifiedThemeProvider>
    ),
  },
  ],
});

const routes = (
  <FlatRoutes>
    <Navigate key="/" to={localStorage.getItem('backstageHomePage') === 'true' ? '/my-space' : '/catalog'} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard
          originNames={{ 'plugin:scaffolder': 'Scaffolder' }}
        />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/my-space" element={<MySpacePage />} />
    <Route path="/azure-metrics" element={<AzureMetricsPage />} />
    <Route path="/azure-cost" element={<AzureCostPage />} />
    <Route path="/tech-radar" element={<TechRadarPage width={1500} height={800} />} />
    <Route path="/explore" element={<ExplorePage />}>
      {explorePage}
    </Route>
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/devtools" element={<DevToolsPage />} />
    <Route path="/entity-validation" element={<EntityValidationPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
